import { Component } from './Component.js';
import { BRAND, applyBrand } from '../config/brand.js';
import { ROUNDS, BADGES, PRIVACY, applyContentPack } from '../config/content.js';
import { validateContentPack } from '../utils/validation.js';
import { escHtml } from '../utils/helpers.js';

const SECTIONS = ['brand', 'privacy', 'badges', 'r0', 'r1', 'r2'];
const SECTION_LABELS = { brand: 'Branding', privacy: 'Privacy', badges: 'Badges', r0: 'Round 1', r1: 'Round 2', r2: 'Round 3' };

/**
 * Facilitator-gated content editor. Everything a client customisation needs
 * — narratives, questions, labels, badges, branding, privacy text — editable
 * from the browser and saved as a content pack in the database. No code
 * edits, no redeploy.
 *
 * Rendered in its own overlay, outside the game-state re-render cycle, so
 * live game updates can never wipe half-typed edits. All edits live in
 * `#draft` (same shape as the stored pack) until Save.
 *
 * Text-only by design: structure (field ids, dimension counts, scoring
 * maxima) always comes from code — see content.js applyContentPack.
 */
export class ContentEditor extends Component {
  #section = 'brand';
  #draft = null;
  #status = '';

  /** @param {HTMLElement} el @param {{ firebase: object, onClose: () => void }} props */
  constructor(el, props) {
    super(el, props);
    this.#draft = this.#buildDraft();
  }

  /** Prefill from the *effective* content (defaults + currently saved pack). */
  #buildDraft() {
    return {
      brand: {
        simName: BRAND.simName,
        companyShort: BRAND.companyShort,
        contactLabel: BRAND.contactLabel,
        contactEmail: BRAND.contactEmail,
        copyrightHolder: BRAND.copyrightHolder,
        cssOverrides: {
          '--brand-primary': BRAND.cssOverrides['--brand-primary'] ?? '',
          '--surface-navy':  BRAND.cssOverrides['--surface-navy'] ?? '',
        },
      },
      privacy: PRIVACY,
      badges: BADGES.map(b => ({ label: b.label, icon: b.icon })),
      rounds: ROUNDS.map(R => ({
        name: R.name, eyebrow: R.eyebrow, dur: R.dur,
        sit: R.sit, brief: R.brief, bomb: R.bomb, bombDim: R.bombDim,
        bombField: { label: R.bombField.label, hint: R.bombField.hint },
        fields:  R.fields.map(f => ({ label: f.label, hint: f.hint })),
        steward: [...R.steward],
        dims:    [...R.dims],
        ...(R.priorities ? { priorities: R.priorities.map(p => ({ t: p.t, v: p.v })) } : {}),
        ...(R.options    ? { options:    R.options.map(o => ({ t: o.t, d: o.d, r: o.r })) } : {}),
      })),
    };
  }

  template() {
    return `
      <div class="overlay__logo">${escHtml(this.#draft.brand.companyShort)}</div>
      <h1 class="overlay__title">Content Editor</h1>
      <div class="overlay__card overlay__card--wide">
        <p class="muted" style="font-size:12px;line-height:1.6;margin-bottom:12px">
          Customise every piece of on-screen text — round narratives, questions, labels, badges,
          branding and the privacy statement. Changes are saved for all devices; already-connected
          devices apply them when their page next reloads. Leave a field blank to use the built-in default.
        </p>
        <div class="ce-tabs" role="tablist" aria-label="Editor sections">
          ${SECTIONS.map(s => `
            <button class="ce-tab${s === this.#section ? ' ce-tab--active' : ''}" role="tab"
              aria-selected="${s === this.#section}" data-ce-tab="${s}">${SECTION_LABELS[s]}</button>`).join('')}
        </div>
        <div class="ce-body">${this.#renderSection()}</div>
        <div class="ce-actions">
          <button class="btn btn--primary btn--sm" id="ce-save">💾 Save to all devices</button>
          <button class="btn btn--sm ce-btn-ghost" id="ce-export">⬇ Export JSON</button>
          <button class="btn btn--sm ce-btn-ghost" id="ce-import">⬆ Import JSON</button>
          <button class="btn btn--sm ce-btn-ghost" id="ce-defaults">↺ Restore defaults</button>
          <button class="btn btn--sm ce-btn-ghost" id="ce-close" style="margin-left:auto">✕ Close</button>
          <input type="file" id="ce-file" accept=".json,application/json" hidden aria-hidden="true">
        </div>
        <p class="ce-status" id="ce-status" role="status" aria-live="polite">${escHtml(this.#status)}</p>
      </div>
    `;
  }

  #renderSection() {
    if (this.#section === 'brand')   return this.#renderBrand();
    if (this.#section === 'privacy') return this.#renderPrivacy();
    if (this.#section === 'badges')  return this.#renderBadges();
    return this.#renderRound(parseInt(this.#section.slice(1), 10));
  }

  #input(bind, value, { label, hint = '', area = false, type = 'text', rows = 3 } = {}) {
    const id = `ce_${bind.replace(/[^a-zA-Z0-9]/g, '_')}`;
    const field = area
      ? `<textarea class="overlay__input overlay__input--area" id="${id}" data-bind="${bind}" rows="${rows}">${escHtml(value ?? '')}</textarea>`
      : `<input class="overlay__input" id="${id}" type="${type}" data-bind="${bind}" ${type === 'number' ? 'min="1" max="120" data-type="number"' : ''} value="${escHtml(value ?? '')}">`;
    return `<div class="ce-field">
      <label class="ce-label" for="${id}">${escHtml(label)}</label>
      ${field}
      ${hint ? `<p class="ce-hint">${escHtml(hint)}</p>` : ''}
    </div>`;
  }

  #renderBrand() {
    const b = this.#draft.brand;
    return `
      ${this.#input('brand.simName', b.simName, { label: 'Simulation name', hint: 'Shown as the page title, on every overlay, and on printed debriefs.' })}
      ${this.#input('brand.companyShort', b.companyShort, { label: 'Short brand mark' })}
      ${this.#input('brand.contactLabel', b.contactLabel, { label: 'Contact name' })}
      ${this.#input('brand.contactEmail', b.contactEmail, { label: 'Contact email' })}
      ${this.#input('brand.copyrightHolder', b.copyrightHolder, { label: 'Copyright holder' })}
      ${this.#input('brand.cssOverrides.--brand-primary', b.cssOverrides['--brand-primary'], { label: 'Primary colour', hint: 'Hex value, e.g. #a28231 — blank keeps the default theme colour.' })}
      ${this.#input('brand.cssOverrides.--surface-navy', b.cssOverrides['--surface-navy'], { label: 'Dark surface colour', hint: 'Hex value, e.g. #0d1b2e — blank keeps the default.' })}
    `;
  }

  #renderPrivacy() {
    return this.#input('privacy', this.#draft.privacy, {
      label: 'Privacy statement', area: true, rows: 12,
      hint: 'Shown before Team Setup. Blank lines separate paragraphs.',
    });
  }

  #renderBadges() {
    return this.#draft.badges.map((b, i) => `
      <div class="ce-row">
        ${this.#input(`badges.${i}.icon`, b.icon, { label: `Badge ${i + 1} icon` })}
        ${this.#input(`badges.${i}.label`, b.label, { label: `Badge ${i + 1} label` })}
      </div>`).join('');
  }

  #renderRound(i) {
    const r = this.#draft.rounds[i];
    return `
      <div class="ce-row">
        ${this.#input(`rounds.${i}.name`, r.name, { label: 'Round name' })}
        ${this.#input(`rounds.${i}.dur`, r.dur, { label: 'Duration (minutes)', type: 'number' })}
      </div>
      ${this.#input(`rounds.${i}.eyebrow`, r.eyebrow, { label: 'Eyebrow line' })}
      ${this.#input(`rounds.${i}.sit`, r.sit, { label: 'Situation briefing', area: true, rows: 6 })}
      ${this.#input(`rounds.${i}.brief`, r.brief, { label: 'Submission brief', area: true, rows: 6, hint: 'Basic HTML allowed (<strong>, <br>).' })}
      ${this.#input(`rounds.${i}.bomb`, r.bomb, { label: 'Bomb drop text', area: true, rows: 3 })}
      <div class="ce-row">
        ${this.#input(`rounds.${i}.bombField.label`, r.bombField.label, { label: 'Bomb response field label' })}
        ${this.#input(`rounds.${i}.bombField.hint`, r.bombField.hint, { label: 'Bomb response field hint' })}
      </div>
      <div class="ce-group">Submission fields</div>
      ${r.fields.map((f, j) => `
        <div class="ce-row">
          ${this.#input(`rounds.${i}.fields.${j}.label`, f.label, { label: `Field ${j + 1} label` })}
          ${this.#input(`rounds.${i}.fields.${j}.hint`, f.hint, { label: `Field ${j + 1} hint` })}
        </div>`).join('')}
      <div class="ce-group">Steward check questions</div>
      ${r.steward.map((q, j) => this.#input(`rounds.${i}.steward.${j}`, q, { label: `Question ${j + 1}`, area: true, rows: 2 })).join('')}
      <div class="ce-group">Scoring dimensions (shown to scorers)</div>
      ${r.dims.map((d, j) => this.#input(`rounds.${i}.dims.${j}`, d, { label: `Dimension ${j + 1}` })).join('')}
      ${this.#input(`rounds.${i}.bombDim`, r.bombDim, { label: 'Bomb scoring dimension' })}
      ${r.priorities ? `
        <div class="ce-group">Priority table</div>
        ${r.priorities.map((p, j) => `
          <div class="ce-row">
            ${this.#input(`rounds.${i}.priorities.${j}.t`, p.t, { label: `Priority ${j + 1}` })}
            ${this.#input(`rounds.${i}.priorities.${j}.v`, p.v, { label: `Priority ${j + 1} — the tension` })}
          </div>`).join('')}` : ''}
      ${r.options ? `
        <div class="ce-group">Investment options</div>
        ${r.options.map((o, j) => `
          ${this.#input(`rounds.${i}.options.${j}.t`, o.t, { label: `Option ${j + 1} title` })}
          <div class="ce-row">
            ${this.#input(`rounds.${i}.options.${j}.d`, o.d, { label: `Option ${j + 1} description` })}
            ${this.#input(`rounds.${i}.options.${j}.r`, o.r, { label: `Option ${j + 1} risk` })}
          </div>`).join('')}` : ''}
    `;
  }

  bindEvents() {
    this.$$('[data-ce-tab]').forEach(btn => {
      const handler = () => { this.#section = btn.dataset.ceTab; this.mount(); };
      btn.addEventListener('click', handler);
      this.addCleanup(() => btn.removeEventListener('click', handler));
    });

    this.$$('[data-bind]').forEach(el => {
      const handler = () => {
        const raw = el.dataset.type === 'number' ? parseFloat(el.value) : el.value;
        this.#set(el.dataset.bind, raw);
      };
      el.addEventListener('input', handler);
      this.addCleanup(() => el.removeEventListener('input', handler));
    });

    this.#on('#ce-save',     () => this.#save());
    this.#on('#ce-export',   () => this.#export());
    this.#on('#ce-import',   () => this.$('#ce-file')?.click());
    this.#on('#ce-defaults', () => this.#restoreDefaults());
    this.#on('#ce-close',    () => this.props.onClose?.());

    const file = this.$('#ce-file');
    if (file) {
      const handler = e => this.#importFile(e.target.files?.[0]);
      file.addEventListener('change', handler);
      this.addCleanup(() => file.removeEventListener('change', handler));
    }
  }

  #on(sel, handler) {
    const el = this.$(sel);
    if (!el) return;
    el.addEventListener('click', handler);
    this.addCleanup(() => el.removeEventListener('click', handler));
  }

  /** Write a value into #draft at a dot-separated path, e.g. "rounds.1.fields.0.label". */
  #set(path, value) {
    const keys = path.split('.');
    let obj = this.#draft;
    for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
    obj[keys.at(-1)] = value;
  }

  #setStatus(msg) {
    this.#status = msg;
    const el = this.$('#ce-status');
    if (el) el.textContent = msg;
  }

  async #save() {
    const pack = JSON.parse(JSON.stringify(this.#draft));
    const { ok, errors } = validateContentPack(pack);
    if (!ok) { this.#setStatus(`Cannot save: ${errors.join('; ')}`); return; }
    const btn = this.$('#ce-save');
    if (btn) btn.disabled = true;
    try {
      await this.props.firebase.writeContentPack(pack);
      applyContentPack(pack);
      applyBrand();
      this.#setStatus('✓ Saved — this device is updated now; other connected devices apply it when their page next reloads.');
    } catch (e) {
      console.error('[ContentEditor] save failed:', e);
      this.#setStatus('Save failed — check connection and try again.');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  #export() {
    const blob = new Blob([JSON.stringify(this.#draft, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'content-pack.json';
    a.click();
    URL.revokeObjectURL(a.href);
    this.#setStatus('Exported content-pack.json — keep it as a versioned backup per client.');
  }

  async #importFile(file) {
    if (!file) return;
    let pack;
    try {
      pack = JSON.parse(await file.text());
    } catch {
      this.#setStatus('Import failed: that file is not valid JSON.');
      return;
    }
    const { ok, errors } = validateContentPack(pack);
    if (!ok) { this.#setStatus(`Import failed: ${errors.join('; ')}`); return; }
    this.#mergeIntoDraft(pack);
    this.mount();
    this.#setStatus('Imported — review the sections, then press Save to publish.');
  }

  /** Overlay an imported pack's text values onto the draft (positional, text-only). */
  #mergeIntoDraft(pack) {
    const d = this.#draft;
    const put = (obj, key, val) => { if (typeof val === 'string' && val.trim()) obj[key] = val; };
    for (const k of ['simName', 'companyShort', 'contactLabel', 'contactEmail', 'copyrightHolder']) {
      put(d.brand, k, pack.brand?.[k]);
    }
    for (const k of ['--brand-primary', '--surface-navy']) {
      put(d.brand.cssOverrides, k, pack.brand?.cssOverrides?.[k]);
    }
    put(d, 'privacy', pack.privacy);
    d.badges.forEach((b, i) => { put(b, 'label', pack.badges?.[i]?.label); put(b, 'icon', pack.badges?.[i]?.icon); });
    d.rounds.forEach((r, i) => {
      const o = pack.rounds?.[i];
      if (!o) return;
      for (const k of ['name', 'eyebrow', 'sit', 'brief', 'bomb', 'bombDim']) put(r, k, o[k]);
      if (Number.isFinite(o.dur)) r.dur = o.dur;
      put(r.bombField, 'label', o.bombField?.label);
      put(r.bombField, 'hint',  o.bombField?.hint);
      r.fields.forEach((f, j) => { put(f, 'label', o.fields?.[j]?.label); put(f, 'hint', o.fields?.[j]?.hint); });
      r.steward.forEach((_, j) => put(r.steward, j, o.steward?.[j]));
      r.dims.forEach((_, j) => put(r.dims, j, o.dims?.[j]));
      r.priorities?.forEach((p, j) => { put(p, 't', o.priorities?.[j]?.t); put(p, 'v', o.priorities?.[j]?.v); });
      r.options?.forEach((opt, j) => { put(opt, 't', o.options?.[j]?.t); put(opt, 'd', o.options?.[j]?.d); put(opt, 'r', o.options?.[j]?.r); });
    });
  }

  async #restoreDefaults() {
    if (!confirm('Restore the built-in default content for all devices?\nThe saved content pack will be removed. Export it first if you want a backup.')) return;
    try {
      await this.props.firebase.clearContentPack();
      applyContentPack(null);
      applyBrand();
      this.#draft = this.#buildDraft();
      this.mount();
      this.#setStatus('✓ Defaults restored — other devices apply them when their page next reloads.');
    } catch (e) {
      console.error('[ContentEditor] restore failed:', e);
      this.#setStatus('Restore failed — check connection and try again.');
    }
  }
}
